import { GraphQLResult } from "@aws-amplify/api-graphql";
import { toast } from "@indigo-components";
import { Notes, NotesType } from "~/api/API";
import { createNotesReturningID } from "~/api/graphql-custom/mutations";
import { errorReporter } from "~/error-handling/errorReporter";
import { GraphQLClientWrapper } from "~/lib/graphql-client";

type CreateNotePayload = {
  createNotes: {
    id: string;
  };
};

interface CreateNotesResult {
  createdNoteIdsByType: { [K in NotesType]?: string[] };
  noteErrorsByType: { [K in NotesType]?: unknown[] };
}

/**
 * Creates notes categorized by their types in parallel.
 *
 * @param client - The GraphQL client to use for the mutation.
 * @param notes - Array of notes to be created.
 * @param newAppointmentID - The ID of the appointment record to associate with the notes.
 * @returns An object containing successfully created note IDs and errors categorized by note types.
 */
export const createNotesByType = async (
  client: GraphQLClientWrapper,
  notes: (Notes | null)[],
  newAppointmentID: string,
  includingTypes?: NotesType[],
): Promise<CreateNotesResult> => {
  // Initialize an array to hold promises with their corresponding note types
  const notePromises: {
    type: NotesType;
    promise: Promise<GraphQLResult<CreateNotePayload>>;
  }[] = [];

  // Step 1: Filter out null notes and create promises categorized by their `type`
  notes
    .filter((note): note is Notes => note !== null)
    .forEach((note) => {
      const noteType: NotesType = note.type || NotesType.GENERAL_MA_NOTES;

      // Skip note types that are excluded
      if (includingTypes && !includingTypes.includes(noteType)) {
        return;
      }

      // Create the GraphQL mutation promise for this note
      const createNotePromise = client.graphql({
        query: createNotesReturningID,
        variables: {
          input: {
            content: note.content,
            notesAuthorId: note.notesAuthorId,
            timeStamp: note.timeStamp,
            appointmentrecordID: newAppointmentID,
            type: note.type,
          },
        },
      });

      // Add the promise along with its type to the array
      notePromises.push({ type: noteType, promise: createNotePromise });
    });

  // Step 2: Execute all promises in parallel using Promise.allSettled
  const results = await Promise.allSettled(
    notePromises.map((np) => np.promise),
  );

  // Initialize objects to hold successful responses and errors categorized by type
  const createdNoteIdsByType: { [K in NotesType]?: string[] } = {};
  const noteErrorsByType: { [K in NotesType]?: unknown[] } = {};

  // Step 3: Iterate through the results and categorize them based on note types
  results.forEach((result, index) => {
    const { type } = notePromises[index];

    if (result.status === "fulfilled") {
      if (result.value.errors) {
        noteErrorsByType[type] = [
          ...(noteErrorsByType[type] || []),
          ...result.value.errors,
        ];
      }
      const createdNoteId = result.value.data?.createNotes.id;
      createdNoteId &&
        (createdNoteIdsByType[type] = [
          ...(createdNoteIdsByType[type] || []),
          createdNoteId,
        ]);
    } else {
      const error = result.reason as Error;
      error &&
        (noteErrorsByType[type] = [...(noteErrorsByType[type] || []), error]);
    }
  });

  for (const [type, errors] of Object.entries(noteErrorsByType)) {
    const err = new Error(
      `Failed to create ${errors.length} notes of type ${type}`,
    );
    errorReporter.reportApplicationError(err, {
      tags: {
        component: "createNotesByType",
        operation: "createNotesByType",
      },
      extra: {
        errors,
        noteType: type,
      },
    });
    toast.error(
      `Failed to save ${errors.length} note${errors.length === 1 ? "" : "s"}`,
    );
  }

  return { createdNoteIdsByType, noteErrorsByType };
};
